import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Autocomplete,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateCalendar, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  createPlannedShifts, 
  updatePlannedShift,
  fetchPlannedShifts,
  fetchPlannedShiftById 
} from '../../redux/slices/plannedShiftsSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const PlannedShiftForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { currentPlannedShift, isLoading: shiftLoading, error: shiftError, actualShifts } = useSelector(state => state.plannedShifts);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  const { vehicles, isLoading: vehiclesLoading } = useSelector(state => state.vehicles);
  const { shifts: appDataShifts } = useSelector(state => state.appData); // Get shifts from AppDataManager
  
  const [formData, setFormData] = useState({
    driverId: '',
    vehicleId: '',
    dates: [],
    endDates: [], // For overnight shifts, track end dates separately
    startTime: dayjs().startOf('hour').add(1, 'hour'),
    plannedDuration: 8,
    notes: '',
    isOvernightShift: false,
    dateEndMapping: {} // Map start dates to end dates for overnight shifts
  });
  
  const [formErrors, setFormErrors] = useState({
    driverId: '',
    vehicleId: '',
    dates: '',
    startTime: ''
  });
  
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  
  // Load planned shifts for availability check
  const { plannedShifts } = useSelector(state => state.plannedShifts);
  
  // Typische Schichtzeiten
  const typicalStartTimes = [
    { label: '06:00', value: '06:00' },
    { label: '07:00', value: '07:00' },
    { label: '08:00', value: '08:00' },
    { label: '14:00', value: '14:00' },
    { label: '15:00', value: '15:00' },
    { label: '22:00', value: '22:00' },
    { label: '23:00', value: '23:00' }
  ];
  
  useEffect(() => {
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
    
    if (isEditMode && id) {
      dispatch(fetchPlannedShiftById(id));
    }
    
    // Load planned shifts for availability check
    // Get a wider date range to check for overlaps
    const startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs().add(1, 'month').format('YYYY-MM-DD');
    dispatch(fetchPlannedShifts({ startDate, endDate }));
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentPlannedShift) {
      const [hours, minutes] = currentPlannedShift.startTime.split(':');
      const startTime = dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
      
      // Handle overnight shifts: extract end dates from dateEndMapping
      let endDates = [];
      let dateEndMapping = {};
      const isOvernightShift = currentPlannedShift.isOvernightShift || false;
      
      if (isOvernightShift && currentPlannedShift.dateEndMapping) {
        // Extract all end dates from the mapping
        endDates = Object.values(currentPlannedShift.dateEndMapping);
        dateEndMapping = currentPlannedShift.dateEndMapping;
      } else if (isOvernightShift && currentPlannedShift.endDate) {
        // Fallback: use single endDate if no mapping available
        endDates = [currentPlannedShift.endDate];
        // Create mapping from dates to endDate
        if (currentPlannedShift.dates && currentPlannedShift.dates.length > 0) {
          currentPlannedShift.dates.forEach(date => {
            dateEndMapping[date] = currentPlannedShift.endDate;
          });
        }
      }
      
      setFormData({
        driverId: currentPlannedShift.driverId || '',
        vehicleId: currentPlannedShift.vehicleId || '',
        dates: currentPlannedShift.dates || [],
        endDates: endDates,
        startTime: startTime,
        plannedDuration: currentPlannedShift.plannedDuration || 8,
        notes: currentPlannedShift.notes || '',
        isOvernightShift: isOvernightShift,
        dateEndMapping: dateEndMapping
      });
    }
  }, [currentPlannedShift, isEditMode]);
  
  // Filter available drivers based on time availability
  useEffect(() => {
    if (drivers && drivers.length > 0) {
      // Only filter by time availability if we have the required data
      if (formData.dates.length > 0 && formData.startTime && formData.plannedDuration > 0) {
        const filteredDrivers = drivers.filter(driver => {
          // Check if driver is available for selected dates and times
          const isTimeAvailable = isDriverAvailable(
            driver.id,
            formData.dates,
            formData.startTime,
            formData.plannedDuration
          );
          
          return isTimeAvailable;
        });
        
        setAvailableDrivers(filteredDrivers);
        
        // Reset driver selection if currently selected driver is no longer available
        if (formData.driverId && !filteredDrivers.find(d => d.id === formData.driverId)) {
          setFormData(prev => ({
            ...prev,
            driverId: ''
          }));
        }
      } else {
        // If no time constraints yet, show all drivers
        setAvailableDrivers(drivers);
      }
    } else {
      setAvailableDrivers([]);
      
      // Reset driver selection if not enabled
      if (!isSelectionEnabled() && formData.driverId) {
        setFormData(prev => ({
          ...prev,
          driverId: ''
        }));
      }
    }
  }, [drivers, formData.dates, formData.startTime, formData.plannedDuration, plannedShifts, actualShifts, appDataShifts, isEditMode, id]);
  
  // Check if vehicle is available for the selected dates and times
  const isVehicleAvailable = (vehicleId, dates, startTime, duration) => {
    if (!plannedShifts || !vehicleId || !dates || dates.length === 0) return true;
    
    // Check each selected date
    for (const date of dates) {
      const shiftDate = dayjs(date);
      const shiftStart = shiftDate.hour(startTime.hour()).minute(startTime.minute());
      const shiftEnd = shiftStart.add(duration, 'hour');
      
      // Check against all planned shifts for this vehicle
      const vehicleShifts = plannedShifts.filter(shift => 
        shift.vehicleId === vehicleId && 
        shift.status !== 'cancelled' &&
        (!isEditMode || shift.id !== id) // Skip current shift when editing
      );
      
      for (const shift of vehicleShifts) {
        // Check all dates of this shift
        const shiftDates = shift.dates || [];
        
        for (const shiftDateStr of shiftDates) {
          const existingShiftDate = dayjs(shiftDateStr);
          const [existingHours, existingMinutes] = shift.startTime.split(':');
          const existingShiftStart = existingShiftDate.hour(parseInt(existingHours)).minute(parseInt(existingMinutes));
          const existingShiftEnd = existingShiftStart.add(shift.plannedDuration, 'hour');
          
          // Determine if existing shift is overnight (spans midnight)
          const isExistingOvernightShift = existingShiftEnd.isAfter(existingShiftStart, 'day');
          
          // Check for overlap scenarios
          let hasOverlap = false;
          
          // Scenario 1: Same date
          if (shiftDate.isSame(existingShiftDate, 'day')) {
            if (isExistingOvernightShift) {
              // Existing shift spans midnight, check overlap until end of day
              const endOfDay = existingShiftDate.hour(23).minute(59).second(59);
              hasOverlap = (
                shiftStart.isBefore(endOfDay.add(1, 'second')) && 
                shiftEnd.isAfter(existingShiftStart)
              );
            } else {
              // Regular same-day overlap check
              hasOverlap = (
                shiftStart.isBefore(existingShiftEnd) && 
                shiftEnd.isAfter(existingShiftStart)
              );
            }
          }
          
          // Scenario 2: Next day of an overnight shift
          else if (shiftDate.isSame(existingShiftDate.add(1, 'day'), 'day') && isExistingOvernightShift) {
            // For overnight shifts, the end time on the next day is the actual end time
            // Example: Fri 22:00 + 8h = Sat 06:00
            const startOfNextDay = shiftDate.hour(0).minute(0).second(0);
            const overnightEndTime = existingShiftEnd; // This is already the correct end time
            
            hasOverlap = (
              shiftStart.isBefore(overnightEndTime) && 
              shiftEnd.isAfter(startOfNextDay)
            );
          }
          
          // Scenario 3: Previous day with new overnight shift extending into existing shift date
          else if (shiftDate.isSame(existingShiftDate.subtract(1, 'day'), 'day')) {
            const isNewOvernightShift = shiftEnd.isAfter(shiftStart, 'day');
            if (isNewOvernightShift) {
              // New shift spans midnight, check overlap from start of existing shift date
              const startOfExistingDay = existingShiftDate.hour(0).minute(0).second(0);
              const newShiftEndTimeNextDay = startOfExistingDay.add(
                shiftEnd.diff(shiftStart.add(1, 'day'), 'minute'), 
                'minute'
              );
              
              hasOverlap = (
                startOfExistingDay.isBefore(existingShiftEnd) && 
                newShiftEndTimeNextDay.isAfter(existingShiftStart)
              );
            }
          }
          
          if (hasOverlap) {
            return false;
          }
        }
      }
      
      // Also check against active shifts from mobile app
      const activeShifts = getActiveShiftsForAvailability();
      for (const activeShift of activeShifts) {
        if (activeShift.vehicleId === vehicleId) {
          // Check if there's overlap between the planned shift and active shift
          const activeStart = activeShift.startTime;
          const activeEnd = activeShift.endTime;
          
          // Check if the active shift overlaps with any of our planned shift dates
          if (shiftDate.isSame(activeStart, 'day') || shiftDate.isSame(activeEnd, 'day')) {
            // Simple overlap check
            const hasOverlap = (
              shiftStart.isBefore(activeEnd) && 
              shiftEnd.isAfter(activeStart)
            );
            
            if (hasOverlap) {
              return false; // Vehicle not available due to active shift
            }
          }
          
          // Check overnight scenarios for active shifts
          if (activeEnd.isAfter(activeStart, 'day')) {
            // Active shift spans multiple days
            const dayAfterActiveStart = activeStart.add(1, 'day').startOf('day');
            if (shiftDate.isSame(dayAfterActiveStart, 'day')) {
              // Check overlap on the next day
              const startOfDay = shiftDate.startOf('day');
              const hasOverlap = (
                shiftStart.isBefore(activeEnd) && 
                shiftEnd.isAfter(startOfDay)
              );
              
              if (hasOverlap) {
                return false; // Vehicle not available due to overnight active shift
              }
            }
          }
        }
      }
    }
    
    return true;
  };
  
  // Check if driver is available for the selected dates and times
  const isDriverAvailable = (driverId, dates, startTime, duration) => {
    if (!plannedShifts || !driverId || !dates || dates.length === 0) return true;
    
    // Check each selected date
    for (const date of dates) {
      const shiftDate = dayjs(date);
      const shiftStart = shiftDate.hour(startTime.hour()).minute(startTime.minute());
      const shiftEnd = shiftStart.add(duration, 'hour');
      
      // Check against all planned shifts for this driver
      const driverShifts = plannedShifts.filter(shift => 
        shift.driverId === driverId && 
        shift.status !== 'cancelled' &&
        (!isEditMode || shift.id !== id) // Skip current shift when editing
      );
      
      for (const shift of driverShifts) {
        // Check all dates of this shift
        const shiftDates = shift.dates || [];
        
        for (const shiftDateStr of shiftDates) {
          const existingShiftDate = dayjs(shiftDateStr);
          const [existingHours, existingMinutes] = shift.startTime.split(':');
          const existingShiftStart = existingShiftDate.hour(parseInt(existingHours)).minute(parseInt(existingMinutes));
          const existingShiftEnd = existingShiftStart.add(shift.plannedDuration, 'hour');
          
          // Determine if existing shift is overnight (spans midnight)
          const isExistingOvernightShift = existingShiftEnd.isAfter(existingShiftStart, 'day');
          
          // Check for overlap scenarios
          let hasOverlap = false;
          
          // Scenario 1: Same date
          if (shiftDate.isSame(existingShiftDate, 'day')) {
            if (isExistingOvernightShift) {
              // Existing shift spans midnight, check overlap until end of day
              const endOfDay = existingShiftDate.hour(23).minute(59).second(59);
              hasOverlap = (
                shiftStart.isBefore(endOfDay.add(1, 'second')) && 
                shiftEnd.isAfter(existingShiftStart)
              );
            } else {
              // Regular same-day overlap check
              hasOverlap = (
                shiftStart.isBefore(existingShiftEnd) && 
                shiftEnd.isAfter(existingShiftStart)
              );
            }
          }
          
          // Scenario 2: Next day of an overnight shift
          else if (shiftDate.isSame(existingShiftDate.add(1, 'day'), 'day') && isExistingOvernightShift) {
            // For overnight shifts, the end time on the next day is the actual end time
            const startOfNextDay = shiftDate.hour(0).minute(0).second(0);
            const overnightEndTime = existingShiftEnd; // This is already the correct end time
            
            hasOverlap = (
              shiftStart.isBefore(overnightEndTime) && 
              shiftEnd.isAfter(startOfNextDay)
            );
          }
          
          // Scenario 3: Previous day with new overnight shift extending into existing shift date
          else if (shiftDate.isSame(existingShiftDate.subtract(1, 'day'), 'day')) {
            const isNewOvernightShift = shiftEnd.isAfter(shiftStart, 'day');
            if (isNewOvernightShift) {
              // New shift spans midnight, check overlap from start of existing shift date
              const startOfExistingDay = existingShiftDate.hour(0).minute(0).second(0);
              const newShiftEndTimeNextDay = startOfExistingDay.add(
                shiftEnd.diff(shiftStart.add(1, 'day'), 'minute'), 
                'minute'
              );
              
              hasOverlap = (
                startOfExistingDay.isBefore(existingShiftEnd) && 
                newShiftEndTimeNextDay.isAfter(existingShiftStart)
              );
            }
          }
          
          if (hasOverlap) {
            return false;
          }
        }
      }
      
      // Also check against active shifts from mobile app
      const activeShifts = getActiveShiftsForAvailability();
      for (const activeShift of activeShifts) {
        if (activeShift.driverId === driverId) {
          // Check if there's overlap between the planned shift and active shift
          const activeStart = activeShift.startTime;
          const activeEnd = activeShift.endTime;
          
          // Check if the active shift overlaps with any of our planned shift dates
          if (shiftDate.isSame(activeStart, 'day') || shiftDate.isSame(activeEnd, 'day')) {
            // Simple overlap check
            const hasOverlap = (
              shiftStart.isBefore(activeEnd) && 
              shiftEnd.isAfter(activeStart)
            );
            
            if (hasOverlap) {
              return false; // Driver not available due to active shift
            }
          }
          
          // Check overnight scenarios for active shifts
          if (activeEnd.isAfter(activeStart, 'day')) {
            // Active shift spans multiple days
            const dayAfterActiveStart = activeStart.add(1, 'day').startOf('day');
            if (shiftDate.isSame(dayAfterActiveStart, 'day')) {
              // Check overlap on the next day
              const startOfDay = shiftDate.startOf('day');
              const hasOverlap = (
                shiftStart.isBefore(activeEnd) && 
                shiftEnd.isAfter(startOfDay)
              );
              
              if (hasOverlap) {
                return false; // Driver not available due to overnight active shift
              }
            }
          }
        }
      }
    }
    
    return true;
  };

  // Check if driver and vehicle selection should be enabled
  const isSelectionEnabled = () => {
    return (
      formData.dates.length > 0 && 
      formData.startTime && 
      formData.plannedDuration > 0
    );
  };

  // Update available vehicles based on selected dates and times
  useEffect(() => {
    // Only filter vehicles if selection is enabled
    if (isSelectionEnabled() && vehicles && vehicles.length > 0) {
      const filteredVehicles = vehicles.filter(vehicle => {
        // Only use time-based availability check - a vehicle that's currently in use
        // can still be available for future shifts at different times
        const isTimeAvailable = isVehicleAvailable(
          vehicle.id,
          formData.dates,
          formData.startTime,
          formData.plannedDuration
        );
        
        return isTimeAvailable;
      });
      
      setAvailableVehicles(filteredVehicles);
      
      // Reset vehicle selection if currently selected vehicle is no longer available
      if (formData.vehicleId && !filteredVehicles.find(v => v.id === formData.vehicleId)) {
        setFormData(prev => ({
          ...prev,
          vehicleId: ''
        }));
      }
    } else {
      setAvailableVehicles([]);
      
      // Reset vehicle selection if not enabled
      if (!isSelectionEnabled() && formData.vehicleId) {
        setFormData(prev => ({
          ...prev,
          vehicleId: ''
        }));
      }
    }
  }, [vehicles, formData.dates, formData.startTime, formData.plannedDuration, plannedShifts, actualShifts, appDataShifts, isEditMode, id]);
  
  // Helper function to calculate end dates and mapping for overnight shifts
  const calculateEndDatesAndMapping = (dates, startTime, duration) => {
    if (!dates || dates.length === 0 || !startTime || !duration) {
      return {
        endDates: [],
        dateEndMapping: {},
        isOvernightShift: false
      };
    }
    
    const endDates = [];
    const dateEndMapping = {};
    let hasAnyOvernightShift = false;
    
    dates.forEach(dateStr => {
      const startDate = dayjs(dateStr);
      const shiftStart = startDate.hour(startTime.hour()).minute(startTime.minute());
      const shiftEnd = shiftStart.add(duration, 'hour');
      
      const isOvernightShift = shiftEnd.isAfter(shiftStart, 'day');
      if (isOvernightShift) {
        hasAnyOvernightShift = true;
      }
      
      const endDateStr = shiftEnd.format('YYYY-MM-DD');
      endDates.push(endDateStr);
      dateEndMapping[dateStr] = endDateStr;
    });
    
    return {
      endDates: [...new Set(endDates)], // Remove duplicates
      dateEndMapping,
      isOvernightShift: hasAnyOvernightShift
    };
  };
  
  // Auto-recalculate end dates when dates, start time, or duration change
  useEffect(() => {
    // Only recalculate if we have the necessary data and we're not in the initial loading phase
    if (formData.dates.length > 0 && formData.startTime && formData.plannedDuration) {
      const { endDates, dateEndMapping, isOvernightShift } = calculateEndDatesAndMapping(
        formData.dates, 
        formData.startTime, 
        formData.plannedDuration
      );
      
      // Only update if the calculated values are different from current values
      const currentEndDatesJson = JSON.stringify(formData.endDates.sort());
      const newEndDatesJson = JSON.stringify(endDates.sort());
      const currentMappingJson = JSON.stringify(formData.dateEndMapping);
      const newMappingJson = JSON.stringify(dateEndMapping);
      
      if (currentEndDatesJson !== newEndDatesJson || 
          currentMappingJson !== newMappingJson || 
          formData.isOvernightShift !== isOvernightShift) {
        
        setFormData(prev => ({
          ...prev,
          endDates,
          dateEndMapping,
          isOvernightShift
        }));
      }
    } else if (formData.dates.length === 0) {
      // Clear end dates if no dates are selected
      setFormData(prev => ({
        ...prev,
        endDates: [],
        dateEndMapping: {},
        isOvernightShift: false
      }));
    }
  }, [formData.dates, formData.startTime, formData.plannedDuration]);
  
  // Helper function to extract active shifts for availability checking
  const getActiveShiftsForAvailability = () => {
    const activeShifts = [];
    
    // Process actualShifts (from mobile app)
    if (actualShifts) {
      Object.entries(actualShifts).forEach(([date, driverData]) => {
        if (driverData && typeof driverData === 'object') {
          Object.entries(driverData).forEach(([driverId, driverShiftData]) => {
            if (driverShiftData && driverShiftData.shifts) {
              driverShiftData.shifts.forEach(shift => {
                if (shift.isActive || (!shift.shiftEnd && shift.shiftStart)) {
                  // Convert Firebase timestamp to dayjs
                  const shiftStartTime = shift.shiftStart?.seconds 
                    ? dayjs.unix(shift.shiftStart.seconds)
                    : dayjs(shift.startTime);
                    
                  // Calculate end time (estimate 8 hours if no end time available)
                  let shiftEndTime;
                  if (shift.shiftEnd && shift.shiftEnd.seconds) {
                    shiftEndTime = dayjs.unix(shift.shiftEnd.seconds);
                  } else if (shift.endTime) {
                    shiftEndTime = dayjs(shift.endTime);
                  } else {
                    // Estimate 8 hours for active shifts
                    shiftEndTime = shiftStartTime.add(8, 'hour');
                  }
                  
                  activeShifts.push({
                    driverId,
                    vehicleId: null, // We'll need to look this up from the driver's assigned vehicle
                    startTime: shiftStartTime,
                    endTime: shiftEndTime,
                    source: 'actualShifts'
                  });
                }
              });
            }
          });
        }
      });
    }
    
    // Process appDataShifts (live data from mobile app)
    if (appDataShifts && appDataShifts.drivers) {
      Object.entries(appDataShifts.drivers).forEach(([driverId, driverShiftData]) => {
        if (driverShiftData && driverShiftData.shifts) {
          driverShiftData.shifts.forEach(shift => {
            if (shift.isActive || (!shift.shiftEnd && shift.shiftStart)) {
              // Convert Firebase timestamp to dayjs
              const shiftStartTime = shift.shiftStart?.seconds 
                ? dayjs.unix(shift.shiftStart.seconds)
                : dayjs(shift.startTime);
                
              // Calculate end time (estimate 8 hours if no end time available)
              let shiftEndTime;
              if (shift.shiftEnd && shift.shiftEnd.seconds) {
                shiftEndTime = dayjs.unix(shift.shiftEnd.seconds);
              } else if (shift.endTime) {
                shiftEndTime = dayjs(shift.endTime);
              } else {
                // Estimate 8 hours for active shifts
                shiftEndTime = shiftStartTime.add(8, 'hour');
              }
              
              // Find assigned vehicle for this driver
              const assignedVehicle = vehicles.find(v => v.current_driver?.id === driverId);
              
              activeShifts.push({
                driverId,
                vehicleId: assignedVehicle?.id || null,
                startTime: shiftStartTime,
                endTime: shiftEndTime,
                source: 'appDataShifts'
              });
            }
          });
        }
      });
    }
    
    return activeShifts;
  };
  
  const handleDriverChange = (event, driver) => {
    setFormData({
      ...formData,
      driverId: driver ? driver.id : ''
    });
    
    if (formErrors.driverId) {
      setFormErrors({
        ...formErrors,
        driverId: ''
      });
    }
  };
  
  const handleVehicleChange = (event, vehicle) => {
    setFormData({
      ...formData,
      vehicleId: vehicle ? vehicle.id : ''
    });
    
    if (formErrors.vehicleId) {
      setFormErrors({
        ...formErrors,
        vehicleId: ''
      });
    }
  };
  
  const handleStartTimeChange = (newTime) => {
    setFormData({
      ...formData,
      startTime: newTime
    });
    
    if (formErrors.startTime) {
      setFormErrors({
        ...formErrors,
        startTime: ''
      });
    }
  };
  
  const handleDurationChange = (event) => {
    setFormData({
      ...formData,
      plannedDuration: event.target.value
    });
  };
  
  const handleDateSelect = (date) => {
    const dateString = date.format('YYYY-MM-DD');
    
    if (formData.dates.includes(dateString)) {
      // Remove date if already selected
      setFormData({
        ...formData,
        dates: formData.dates.filter(d => d !== dateString)
      });
      // Clear calendar selection when removing a date
      setSelectedDate(null);
    } else {
      // Add date if not selected
      setFormData({
        ...formData,
        dates: [...formData.dates, dateString].sort()
      });
      // Update calendar selection to the newly added date
      setSelectedDate(date);
    }
    
    if (formErrors.dates) {
      setFormErrors({
        ...formErrors,
        dates: ''
      });
    }
  };
  
  const handleRemoveDate = (dateToRemove) => {
    setFormData({
      ...formData,
      dates: formData.dates.filter(d => d !== dateToRemove)
    });
    // Clear calendar selection if the removed date was selected
    if (selectedDate && selectedDate.format('YYYY-MM-DD') === dateToRemove) {
      setSelectedDate(null);
    }
  };
  
  const handleQuickTimeSelect = (time) => {
    const [hours, minutes] = time.split(':');
    const newTime = dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
    handleStartTimeChange(newTime);
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Validate driver (now required when selection is enabled)
    if (isSelectionEnabled() && !formData.driverId) {
      newErrors.driverId = 'Bitte wählen Sie einen Fahrer aus';
      valid = false;
    }
    
    // Validate dates
    if (formData.dates.length === 0) {
      newErrors.dates = 'Bitte wählen Sie mindestens einen Tag aus';
      valid = false;
    }
    
    // Validate start time
    if (!formData.startTime) {
      newErrors.startTime = 'Startzeit ist erforderlich';
      valid = false;
    }
    
    // Validate vehicle (now required when selection is enabled)
    if (isSelectionEnabled() && !formData.vehicleId) {
      newErrors.vehicleId = 'Bitte wählen Sie ein Fahrzeug aus';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const shiftData = {
        driverId: formData.driverId,
        vehicleId: formData.vehicleId, // Now required when vehicle selection is enabled
        dates: formData.dates,
        startTime: formData.startTime.format('HH:mm'),
        plannedDuration: formData.plannedDuration,
        notes: formData.notes
      };
      
      // Add overnight shift data
      if (formData.isOvernightShift) {
        shiftData.isOvernightShift = true;
        shiftData.dateEndMapping = formData.dateEndMapping;
        // Add single endDate for backward compatibility (use first end date)
        if (formData.endDates.length > 0) {
          shiftData.endDate = formData.endDates[0];
        }
      } else {
        // Explicitly clear overnight shift data for non-overnight shifts
        shiftData.isOvernightShift = false;
        shiftData.dateEndMapping = {};
        shiftData.endDate = null;
      }
      
      if (isEditMode) {
        // Update existing planned shift
        dispatch(updatePlannedShift({
          id,
          ...shiftData
        })).then((result) => {
          if (!result.error) {
            navigate('/schichtplaner');
          }
        });
      } else {
        // Create new planned shifts
        dispatch(createPlannedShifts(shiftData)).then((result) => {
          if (!result.error) {
            navigate('/schichtplaner');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    navigate('/schichtplaner');
  };
  
  const isLoading = shiftLoading || driversLoading || vehiclesLoading;
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (shiftError && isEditMode) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={shiftError}
        onRetry={() => dispatch(fetchPlannedShifts({ plannedShiftId: id }))}
      />
    );
  }
  
  // Check if date is a start date (for visual distinction)
  const isStartDate = (date) => {
    return formData.dates.includes(date.format('YYYY-MM-DD'));
  };
  
  // Check if date is an end date (for visual distinction)
  const isEndDate = (date) => {
    return formData.endDates.includes(date.format('YYYY-MM-DD'));
  };
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Geplante Schicht bearbeiten' : 'Neue Schicht planen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichtplaner', path: '/schichtplaner' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: '#' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Datumsauswahl */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Tage auswählen
          </Typography>
          {formErrors.dates && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.dates}
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={selectedDate}
                  onChange={setSelectedDate}
                  onMonthChange={setSelectedDate}
                  shouldDisableDate={(date) => date.isBefore(dayjs(), 'day')}
                  sx={{
                    '& .MuiPickersDay-root': {
                      cursor: 'pointer'
                    },
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }
                  }}
                  slotProps={{
                    day: (ownerState) => ({
                      onClick: () => handleDateSelect(ownerState.day),
                      sx: {
                        backgroundColor: (() => {
                          if (isStartDate(ownerState.day)) {
                            return 'primary.main'; // Blue for start dates
                          } else if (isEndDate(ownerState.day)) {
                            return 'warning.main'; // Orange for end dates
                          }
                          return 'transparent';
                        })(),
                        color: (() => {
                          if (isStartDate(ownerState.day)) {
                            return 'primary.contrastText';
                          } else if (isEndDate(ownerState.day)) {
                            return 'warning.contrastText';
                          }
                          return 'inherit';
                        })(),
                        '&:hover': {
                          backgroundColor: (() => {
                            if (isStartDate(ownerState.day)) {
                              return 'primary.dark';
                            } else if (isEndDate(ownerState.day)) {
                              return 'warning.dark';
                            }
                            return 'action.hover';
                          })()
                        },
                        position: 'relative',
                        '&::after': isEndDate(ownerState.day) ? {
                          content: '"E"',
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          opacity: 0.8
                        } : {}
                      }
                    })
                  }}
                />
              </LocalizationProvider>
              
              {/* Calendar Legend for overnight shifts */}
              {formData.isOvernightShift && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" gutterBottom display="block">
                    Legende:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        backgroundColor: 'primary.main',
                        borderRadius: 0.5
                      }} />
                      <Typography variant="caption">Startdaten</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        backgroundColor: 'warning.main',
                        borderRadius: 0.5,
                        position: 'relative'
                      }}>
                        <Typography variant="caption" sx={{
                          position: 'absolute',
                          top: '-2px',
                          right: '0px',
                          fontSize: '0.5rem',
                          fontWeight: 'bold',
                          color: 'warning.contrastText'
                        }}>E</Typography>
                      </Box>
                      <Typography variant="caption">Enddaten</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Ausgewählte Tage ({formData.dates.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {formData.dates.length === 0 ? (
                <Typography color="textSecondary">
                  Klicken Sie auf Tage im Kalender, um sie auszuwählen
                </Typography>
              ) : (
                <List dense>
                  {formData.dates.map((date) => (
                    <ListItem key={date}>
                      <ListItemText 
                        primary={dayjs(date).format('dddd, DD.MM.YYYY')}
                        secondary={(() => {
                          const startTime = formData.startTime;
                          const endTime = startTime.add(formData.plannedDuration, 'hour');
                          const isNextDay = endTime.isAfter(startTime, 'day');
                          
                          let timeDisplay = isNextDay 
                            ? `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')} Uhr (+1 Tag)`
                            : `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')} Uhr`;
                          
                          // Add end date info for overnight shifts
                          if (formData.isOvernightShift && formData.dateEndMapping[date]) {
                            const endDate = formData.dateEndMapping[date];
                            const endDateFormatted = dayjs(endDate).format('DD.MM.YYYY');
                            timeDisplay += ` → Enddatum: ${endDateFormatted}`;
                          }
                          
                          return timeDisplay;
                        })()}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => handleRemoveDate(date)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Zeit und Dauer */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Zeit und Dauer
          </Typography>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Schnellauswahl Startzeit
              </Typography>
              <Box sx={{ mb: 2 }}>
                {typicalStartTimes.map((time) => (
                  <Button
                    key={time.value}
                    variant={formData.startTime.format('HH:mm') === time.value ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleQuickTimeSelect(time.value)}
                    sx={{ mr: 1, mb: 1 }}
                    startIcon={<TimeIcon />}
                  >
                    {time.label}
                  </Button>
                ))}
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="Startzeit"
                  value={formData.startTime}
                  onChange={handleStartTimeChange}
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.startTime,
                      helperText: formErrors.startTime
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Geplante Dauer (Stunden)</InputLabel>
                <Select
                  value={formData.plannedDuration}
                  onChange={handleDurationChange}
                  label="Geplante Dauer (Stunden)"
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1} {i === 0 ? 'Stunde' : 'Stunden'}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Standard: 8 Stunden
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Schicht-Informationen */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Schicht-Informationen
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {isSelectionEnabled() ? (
                <>
                  <Autocomplete
                    id="driver-select"
                    options={availableDrivers}
                    getOptionLabel={(option) => option.user?.name || option.name || 'Unbekannter Fahrer'}
                    value={availableDrivers.find(d => d.id === formData.driverId) || null}
                    onChange={handleDriverChange}
                    disabled={availableDrivers.length === 0}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Fahrer *" 
                        variant="outlined"
                        error={!!formErrors.driverId}
                        helperText={formErrors.driverId || (availableDrivers.length === 0 ? 'Keine Fahrer für die gewählte Zeit verfügbar' : '')}
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1">{option.user?.name || option.name || 'Unbekannter Fahrer'}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {option.user?.phone || option.phone || 'Keine Telefonnummer'} | Fahrer-Nr: {option.driverNumber || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                  <FormHelperText>
                    {availableDrivers.length > 0 
                      ? `${availableDrivers.length} Fahrer verfügbar für die gewählte Zeit`
                      : 'Keine Fahrer verfügbar - bitte Zeit oder Datum ändern'
                    }
                  </FormHelperText>
                </>
              ) : (
                <TextField
                  label="Fahrer"
                  variant="outlined"
                  disabled
                  placeholder="Bitte wählen Sie zuerst Datum und Uhrzeit"
                  helperText="Fahrerauswahl wird nach Datum- und Uhrzeitauswahl aktiviert"
                />
              )}
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              {isSelectionEnabled() ? (
                <>
                  <Autocomplete
                    id="vehicle-select"
                    options={availableVehicles}
                    getOptionLabel={(option) => `${option.model} (${option.licensePlate || option.plate})`}
                    value={availableVehicles.find(v => v.id === formData.vehicleId) || null}
                    onChange={handleVehicleChange}
                    disabled={availableVehicles.length === 0}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Fahrzeug *" 
                        variant="outlined"
                        error={!!formErrors.vehicleId}
                        helperText={formErrors.vehicleId || (availableVehicles.length === 0 ? 'Keine Fahrzeuge für die gewählte Zeit verfügbar' : '')}
                        required
                      />
                    )}
                  />
                  <FormHelperText>
                    {availableVehicles.length > 0 
                      ? `${availableVehicles.length} Fahrzeug(e) verfügbar für die gewählte Zeit`
                      : 'Keine Fahrzeuge verfügbar - bitte Zeit oder Datum ändern'
                    }
                  </FormHelperText>
                </>
              ) : (
                <TextField
                  label="Fahrzeug"
                  variant="outlined"
                  disabled
                  placeholder="Bitte wählen Sie zuerst Datum und Uhrzeit"
                  helperText="Fahrzeugauswahl wird nach Datum- und Uhrzeitauswahl aktiviert"
                />
              )}
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Notizen */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Zusätzliche Informationen
          </Typography>
          <TextField
            fullWidth
            id="notes"
            name="notes"
            label="Notizen"
            multiline
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            helperText="Optionale Notizen zu dieser Schichtplanung"
          />
        </FormPaper>
        
        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isEditMode ? 'Änderungen speichern' : `${formData.dates.length} Schicht${formData.dates.length !== 1 ? 'en' : ''} planen`}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PlannedShiftForm;